/**
 * Signature Panel (Schritt 5)
 *
 * Die Nachrichten-Signatur ist eine ACCOUNT-Einstellung: sie wird an
 * ausgehende Kampagnen-Nachrichten angehängt, wenn der jeweilige Schritt
 * „Signatur anhängen" aktiviert hat. Ohne gepflegte Signatur fällt das
 * System auf einen generischen Gruß mit dem Account-Namen zurück.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, MenuItem, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { getAllLinkedinAccounts, updateLinkedinAccountSignature } from '../../../API/linkedin-accounts';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';

interface SignaturePanelProps {
  onSaved: (summary: string) => void;
  /** Alle Accounts haben eine Signatur — der Wizard geht zum nächsten Schritt. */
  onComplete?: () => void;
  /** Signatur ist optional: leer lässt sich der Schritt überspringen (wie die Blacklist). */
  onSkip?: () => void;
}

interface AccountRow {
  id: string;
  label: string;
  signature: string;
}

const MAX_LENGTH = 500;

const SignaturePanel: React.FC<SignaturePanelProps> = ({ onSaved, onComplete, onSkip }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const dataVersion = useAIChatStore((state) => state.dataVersion);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState('');
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllLinkedinAccounts();
        if (cancelled) return;
        const rows: AccountRow[] = (res?.allLinkedInAccounts || []).map((a: any) => ({
          id: a._id,
          label: [a.firstName, a.lastName].filter(Boolean).join(' ') || a.linkedinEmail || a._id,
          signature: a.signature || '',
        }));
        setAccounts(rows);
        const current = rows.find((r) => r.id === accountId) || rows[0];
        if (current) {
          setAccountId(current.id);
          setSignature(current.signature);
        }
      } catch {
        if (!cancelled) setError(isDe ? 'Accounts konnten nicht geladen werden.' : 'Could not load accounts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Neu laden, wenn der Bot Daten geändert haben könnte (nach jedem Chat-Turn).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion]);

  const currentAccount = accounts.find((a) => a.id === accountId);
  const unconfigured = useMemo(() => accounts.filter((a) => !a.signature.trim()), [accounts]);
  const canSave = !!accountId && signature.trim().length > 0 && signature.length <= MAX_LENGTH && !saving;

  const handleSave = async () => {
    if (!canSave || !currentAccount) return;
    setSaving(true);
    setError(null);
    try {
      const res: any = await updateLinkedinAccountSignature(accountId, signature.trim());
      if (res?.error) throw new Error('save failed');
      setAccounts((prev) =>
        prev.map((a) => (a.id === accountId ? { ...a, signature: signature.trim() } : a))
      );
      setSaved(true);
      onSaved(
        isDe
          ? `Ich habe die Signatur für den LinkedIn-Account „${currentAccount.label}" im Formular gespeichert. Bitte bestätige kurz und schlage mir die nächsten Schritte vor.`
          : `I just saved the signature for the LinkedIn account "${currentAccount.label}" in the form. Please confirm briefly and suggest my next steps.`
      );
      // Weiter: zum nächsten Account ohne Signatur, sonst zum nächsten Schritt.
      const remaining = accounts.filter((a) => a.id !== accountId && !a.signature.trim());
      if (remaining.length > 0) {
        setAccountId(remaining[0].id);
        setSignature(remaining[0].signature);
        setSaved(false);
      } else onComplete?.();
    } catch {
      setError(isDe ? 'Speichern fehlgeschlagen — bitte erneut versuchen.' : 'Saving failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (accounts.length === 0) {
    return (
      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {isDe
            ? 'Noch kein LinkedIn-Account verbunden — schließe zuerst Schritt 2 ab.'
            : 'No LinkedIn account connected yet — complete step 2 first.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {unconfigured.length > 0 && !saved ? (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe
              ? `Fehlt in diesem Schritt: Signatur für ${unconfigured.map((a) => a.label).join(', ')}`
              : `Missing in this step: signature for ${unconfigured.map((a) => a.label).join(', ')}`}
          </Typography>
        </Box>
      ) : (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {isDe ? 'Signatur gespeichert' : 'Signature saved'}
          </Typography>
        </Box>
      )}

      {accounts.length > 1 && (
        <Box className={classes.card}>
          <Typography variant="caption" className={classes.sectionLabel}>
            {isDe ? 'LinkedIn-Account' : 'LinkedIn account'}
          </Typography>
          <TextField
            size="small"
            fullWidth
            select
            value={accountId}
            onChange={(e) => {
              const row = accounts.find((a) => a.id === e.target.value);
              if (row) {
                setAccountId(row.id);
                setSignature(row.signature);
                setSaved(false);
              }
            }}
            helperText={
              isDe
                ? 'Jeder Account hat seine eigene Signatur.'
                : 'Each account has its own signature.'
            }
          >
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.label} {a.signature.trim() ? '✓' : `— ${isDe ? 'noch offen' : 'not set'}`}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Box className={classes.card}>
        <Typography variant="caption" className={classes.sectionLabel}>
          {isDe ? 'LinkedIn Nachrichten-Signatur' : 'LinkedIn message signature'}
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={4}
          placeholder={isDe ? 'z.B.\nViele Grüße\nMax Mustermann\nACME GmbH' : 'e.g.\nBest regards\nJohn Doe\nACME Inc.'}
          value={signature}
          onChange={(e) => {
            setSignature(e.target.value.slice(0, MAX_LENGTH));
            setSaved(false);
          }}
          helperText={`${signature.length}/${MAX_LENGTH}`}
        />
        <Typography variant="caption" color="textSecondary">
          {isDe
            ? 'Die Signatur wird an deine LinkedIn-Kampagnen-Nachrichten angehängt, wenn im jeweiligen Schritt „Signatur anhängen" aktiviert ist. Ohne Signatur nutzt das System einen generischen Gruß mit deinem Namen.'
            : 'The signature is appended to campaign messages when the step has "add signature" enabled. Without one, the system falls back to a generic sign-off with your name.'}
        </Typography>
      </Box>

      {error && (
        <Typography variant="caption" className={classes.error}>
          {error}
        </Typography>
      )}

      <Box className={classes.footer}>
        <Typography variant="caption" className={classes.footerHint}>
          {saved
            ? isDe
              ? 'Gespeichert — der Assistent meldet sich im Chat.'
              : 'Saved — the assistant will follow up in the chat.'
            : isDe
              ? 'Kurz und persönlich wirkt am besten.'
              : 'Short and personal works best.'}
        </Typography>
        {/* Ein Knopf: leer → überspringen, ausgefüllt → speichern und weiter. */}
        {!signature.trim() && onSkip ? (
          <button type="button" className={classes.saveButton} onClick={onSkip}>
            {isDe ? 'Überspringen' : 'Skip'}
          </button>
        ) : (
          <button type="button" className={classes.saveButton} onClick={handleSave} disabled={!canSave}>
            {saving ? (isDe ? 'Speichert…' : 'Saving…') : isDe ? 'Weiter' : 'Next'}
          </button>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: dc.muted,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default SignaturePanel;
